import { motion, useInView, useMotionValue, useTransform, animate, AnimatePresence } from "framer-motion";
import { useEffect, useRef } from "react";

// Smooth easing curve used throughout
export const easeLuxury = [0.22, 1, 0.36, 1];

// Reveal block when it scrolls into view
export function Reveal({ children, delay = 0, y = 30, className = "", as = "div", once = true }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-80px" });
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.9, delay, ease: easeLuxury }}
      className={className}
    >
      {children}
    </MotionTag>
  );
}

// Staggered children container
export function StaggerContainer({ children, className = "", delay = 0.1, stagger = 0.12 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export const staggerItem = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: easeLuxury } },
};

// Animated number counter
export function CountUp({ to, duration = 2, prefix = "", suffix = "", className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const value = useMotionValue(0);
  const display = useTransform(value, (latest) => {
    const n = Math.floor(latest);
    return `${prefix}${n.toLocaleString("en-IN")}${suffix}`;
  });

  useEffect(() => {
    if (inView) {
      const controls = animate(value, to, { duration, ease: easeLuxury });
      return controls.stop;
    }
  }, [inView, to, duration, value]);

  return <motion.span ref={ref} className={className}>{display}</motion.span>;
}

// Text that reveals char-by-char
export function RevealText({ text, className = "", delay = 0 }) {
  const words = text.split(" ");
  return (
    <span className={className}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          className="inline-block mr-[0.25em]"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: delay + i * 0.08, ease: easeLuxury }}
        >
          {word}
        </motion.span>
      ))}
    </span>
  );
}

export { motion, AnimatePresence, useInView };
